// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  // Check if we're in a browser environment before using window
  const fallbackUrl = typeof window !== 'undefined' ? window.location.origin : '';
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : fallbackUrl;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Fetch recent listings from the local API endpoint.
export const recentListings = (params = {}) => {
  const { count = 9 } = params;
  return request(`/api/recent-listings?count=${count}`);
};

// SendCloud shipping API calls

// Create a SendCloud parcel
//
// See `server/api/shipping/sendcloud-create-parcel.js` to see what data should
// be sent in the body.
export const createSendCloudParcel = body => {
  return request('/api/shipping/sendcloud-create-parcel', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Get SendCloud parcel details by shipment ID
export const getSendCloudParcel = shipmentId => {
  return request(`/api/shipping/sendcloud-get-parcel?shipmentId=${shipmentId}`);
};

// Get SendCloud shipments by external reference ID (transaction ID)
export const getSendCloudShipmentByReference = externalReferenceId => {
  return request(`/api/shipping/sendcloud-get-shipment-by-reference?externalReferenceId=${encodeURIComponent(externalReferenceId)}`);
};

// Track a SendCloud parcel by tracking number
export const trackSendCloudParcel = trackingNumber => {
  return request(`/api/shipping/sendcloud-track?trackingNumber=${trackingNumber}`);
};

// Get SendCloud shipping methods
// Accepts optional query parameters: to_country, from_country, weight, etc.
export const getSendCloudShippingMethods = (params = {}) => {
  const queryString = Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  const endpoint = queryString
    ? `/api/shipping/sendcloud-shipping-methods?${queryString}`
    : '/api/shipping/sendcloud-shipping-methods';
  return request(endpoint);
};

// Get available SendCloud shipping carriers for a package size tier and origin country
export const getSendCloudShippingProducts = (params = {}) => {
  const queryString = Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  return request(`/api/shipping/sendcloud-shipping-products?${queryString}`);
};

// Get SendCloud shipping price for a specific method, origin, destination, and package size
export const getSendCloudShippingPrice = (params = {}) => {
  const queryString = Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  return request(`/api/shipping/sendcloud-shipping-price?${queryString}`);
};

// Download SendCloud parcel label as PDF
// Returns a Blob for PDF download
export const downloadSendCloudLabel = body => {
  const url = `${apiBaseUrl()}/api/shipping/sendcloud-get-label`;

  return window.fetch(url, {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    // Return the blob directly for PDF
    return res.blob();
  });
};
